import { lazy, Suspense, useState } from "react";
import { Toaster, SonnerToaster, TooltipProvider } from "@cieda/djayma-ui";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";

import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { handleQueryError } from "@/lib/api-error-handler";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { CartLifecycle } from "@/components/CartLifecycle";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import AuthGuard from "./components/auth/AuthGuard";
import AdminGuard from "./components/auth/AdminGuard";
import AdminModuleProvider from "@/components/admin/AdminModuleProvider";
import { FlagProvider } from "@/lib/feature-flags";
import DriverAuthGuard from "@/pages/delivery-portal/components/DriverAuthGuard";
// Lazy-load non-critical UI widgets — both render on every page but are below-the-fold
// and can be deferred until after the main bundle parses
const NotFound = lazy(() => import("./pages/NotFound"));
const Contact = lazy(() => import("./pages/Contact"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
const ChatWidget = lazy(() => import("@/components/chat/ChatWidget").then((m) => ({ default: m.ChatWidget })));

// Lazy-loaded pages — reduces initial bundle for public visitors
// Index is the heaviest page (all home sections) so it is lazy too
const Index = lazy(() => import("./pages/Index"));
const Cart = lazy(() => import("./pages/Cart"));
const Livraison = lazy(() => import("./pages/Livraison"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Tracking = lazy(() => import("./pages/Tracking"));
const Receipt = lazy(() => import("./pages/Receipt"));
const Login = lazy(() => import("./pages/account/Login"));
const Register = lazy(() => import("./pages/account/Register"));
const ResetRequest = lazy(() => import("./pages/account/ResetRequest"));
const ResetPassword = lazy(() => import("./pages/account/ResetPassword"));
const Profile = lazy(() => import("./pages/account/Profile"));
const Orders = lazy(() => import("./pages/account/Orders"));
const Addresses = lazy(() => import("./pages/account/Addresses"));
const Returns = lazy(() => import("./pages/account/Returns"));
const Invoices = lazy(() => import("./pages/account/Invoices"));
const Wishlist = lazy(() => import("./pages/account/Wishlist"));
const OrderDetail = lazy(() => import("./pages/account/OrderDetail"));
const AccountSettings = lazy(() => import("./pages/account/Settings"));
const EmailVerification = lazy(() => import("./pages/account/EmailVerification"));
const Catalogue = lazy(() => import("./pages/Catalogue"));
const SharedWishlist = lazy(() => import("./pages/SharedWishlist"));
const PolitiqueConfidentialite = lazy(() => import("./pages/PolitiqueConfidentialite"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Conditions = lazy(() => import("./pages/Conditions"));
const PolitiqueRetours = lazy(() => import("./pages/PolitiqueRetours"));
const MarketingOptOut = lazy(() => import("./pages/MarketingOptOut"));
// Scaffold: driver POD webview — deny placeholder for now (see #104 Phase C).
const DriverHandoff = lazy(() => import("./pages/DriverHandoff"));

// ---------------------------------------------------------------------------
// Admin pages — existing implementations
// ---------------------------------------------------------------------------
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("./pages/admin/OrderManagement"));
const AdminClients = lazy(() => import("./pages/admin/ClientsCRM"));
const AdminFinances = lazy(() => import("./pages/admin/FinanceDashboard"));
const AdminLogistics = lazy(() => import("./pages/admin/LogisticsManagement"));
const AdminReturns = lazy(() => import("./pages/admin/ReturnsManagement"));
const AdminInvoices = lazy(() => import("./pages/admin/InvoiceManagement"));
const AdminSettings = lazy(() => import("./pages/admin/Settings"));

// DJAYMA engine consoles (shared src, also routed in djayma-admin)
const DjaymaControlTower = lazy(() => import("./pages/admin/djayma/ControlTower"));
const DjaymaFinance = lazy(() => import("./pages/admin/djayma/FinanceReconciliation"));
const DjaymaCallCenter = lazy(() => import("./pages/admin/djayma/CallCenter"));
const DjaymaStock = lazy(() => import("./pages/admin/djayma/StockConsole"));
const DjaymaOrders = lazy(() => import("./pages/admin/djayma/OrdersBoard"));
const DjaymaLogistique = lazy(() => import("./pages/admin/djayma/LogistiqueConsole"));
const DjaymaPricing = lazy(() => import("./pages/admin/djayma/PriceBook"));
const DjaymaPromos = lazy(() => import("./pages/admin/djayma/PromoBoard"));
const DjaymaPartners = lazy(() => import("./pages/admin/djayma/PartnersBoard"));
const DjaymaChannels = lazy(() => import("./pages/admin/djayma/ChannelMix"));
const DjaymaAccess = lazy(() => import("./pages/admin/djayma/AccessDirectory"));
const AdminOrderDetail = lazy(() => import("./pages/admin/OrderDetail"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));

// ---------------------------------------------------------------------------
// Admin pages — Commerce section placeholders
// ---------------------------------------------------------------------------
const AdminCommerceOrders = lazy(() => import("./pages/admin/commerce/Orders"));
const AdminProducts = lazy(() => import("./pages/admin/commerce/Products"));
const AdminCategories = lazy(() => import("./pages/admin/commerce/Categories"));
const AdminInventory = lazy(() => import("./pages/admin/commerce/Inventory"));
const AdminStockAlerts = lazy(() => import("./pages/admin/commerce/StockAlerts"));
const AdminNotifications = lazy(() => import("./pages/admin/commerce/Notifications"));

// ---------------------------------------------------------------------------
// Phase 2 — placeholder pages hidden from nav and routing until implemented.
// To restore a section: uncomment its import block here AND uncomment the
// corresponding route block below AND uncomment the nav section in nav-sections.ts.
// ---------------------------------------------------------------------------

// --- Payments ---
const AdminPayments = lazy(() => import("./pages/admin/payments/PaymentManager"));
const AdminReconciliation = lazy(() => import("./pages/admin/payments/Reconciliation"));
const AdminRefunds = lazy(() => import("./pages/admin/payments/Refunds"));
const AdminDisputes = lazy(() => import("./pages/admin/payments/Disputes"));
const AdminDailyCash = lazy(() => import("./pages/admin/payments/DailyCash"));
const AdminCallbackLogs = lazy(() => import("./pages/admin/payments/CallbackLogs"));
const AdminDeadLetterQueue = lazy(() => import("./pages/admin/payments/DeadLetterQueue"));

// --- Delivery ---
const AdminDeliveries = lazy(() => import("./pages/admin/delivery/DeliveryBoard"));
const AdminDrivers = lazy(() => import("./pages/admin/delivery/Drivers"));
const AdminRoutes = lazy(() => import("./pages/admin/delivery/Routes"));
const AdminTracking = lazy(() => import("./pages/admin/delivery/LiveTracking"));
const AdminYango = lazy(() => import("./pages/admin/delivery/Yango"));
const AdminDeliveryZones = lazy(() => import("./pages/admin/delivery/DeliveryZones"));
const AdminDeliverySlots = lazy(() => import("./pages/admin/delivery/DeliverySlots"));
const AdminDepots = lazy(() => import("./pages/admin/delivery/Depots"));
const AdminDeliveryPerformance = lazy(() => import("./pages/admin/delivery/DeliveryPerformance"));
const AdminDeliveryFlags = lazy(() => import("./pages/admin/delivery/Flags"));

// --- B2B ---
const AdminB2BCustomers = lazy(() => import("./pages/admin/b2b/Customers"));
const AdminB2BPriceLists = lazy(() => import("./pages/admin/b2b/PriceLists"));
const AdminB2BCredit = lazy(() => import("./pages/admin/b2b/Credit"));
const AdminB2BApprovals = lazy(() => import("./pages/admin/b2b/Approvals"));
const AdminB2BImport = lazy(() => import("./pages/admin/b2b/Import"));
const AdminB2BInvoices = lazy(() => import("./pages/admin/b2b/Invoices"));
const AdminB2BAging = lazy(() => import("./pages/admin/b2b/Aging"));
const AdminSuppliers = lazy(() => import("./pages/admin/b2b/Suppliers"));
const AdminB2BVolumeDiscounts = lazy(() => import("./pages/admin/b2b/VolumeDiscounts"));
const AdminB2BReps = lazy(() => import("./pages/admin/b2b/Reps"));

// --- CRM ---
const AdminSegments = lazy(() => import("./pages/admin/crm/Segments"));
const AdminLoyalty = lazy(() => import("./pages/admin/crm/Loyalty"));
const AdminWhatsApp = lazy(() => import("./pages/admin/crm/WhatsApp"));
const AdminSMS = lazy(() => import("./pages/admin/crm/SMS"));
const AdminFeedback = lazy(() => import("./pages/admin/crm/Feedback"));
const AdminAbandonedCarts = lazy(() => import("./pages/admin/crm/AbandonedCarts"));
const AdminCLV = lazy(() => import("./pages/admin/crm/CLV"));
const AdminReferrals = lazy(() => import("./pages/admin/crm/Referrals"));
const AdminCommunications = lazy(() => import("./pages/admin/crm/Communications"));

// --- Marketing ---
const AdminPromotions = lazy(() => import("./pages/admin/marketing/Promotions"));
const AdminCoupons = lazy(() => import("./pages/admin/marketing/Coupons"));
const AdminFlashSales = lazy(() => import("./pages/admin/marketing/FlashSales"));
const AdminBundles = lazy(() => import("./pages/admin/marketing/Bundles"));
const AdminBanners = lazy(() => import("./pages/admin/marketing/Banners"));
const AdminNewsletter = lazy(() => import("./pages/admin/marketing/Newsletter"));
const AdminSeasonal = lazy(() => import("./pages/admin/marketing/Seasonal"));
const AdminStackingRules = lazy(() => import("./pages/admin/marketing/StackingRules"));
const AdminPromoAnalytics = lazy(() => import("./pages/admin/marketing/PromoAnalytics"));
const AdminTrackingLinks = lazy(() => import("./pages/admin/marketing/TrackingLinks"));

// --- Finance ---
const AdminRevenue = lazy(() => import("./pages/admin/finance/Revenue"));
const AdminTVA = lazy(() => import("./pages/admin/finance/TVA"));
const AdminMargins = lazy(() => import("./pages/admin/finance/Margins"));
const AdminExpenses = lazy(() => import("./pages/admin/finance/Expenses"));
const AdminFinanceInvoices = lazy(() => import("./pages/admin/finance/Invoices"));
const AdminCashflow = lazy(() => import("./pages/admin/finance/Cashflow"));
const AdminSageExport = lazy(() => import("./pages/admin/finance/SageExport"));
const AdminPowerBI = lazy(() => import("./pages/admin/finance/PowerBI"));
const AdminReports = lazy(() => import("./pages/admin/finance/Reports"));
const AdminDailyPnL = lazy(() => import("./pages/admin/finance/DailyPnL"));

// --- Operations ---
const AdminAdminUsers = lazy(() => import("./pages/admin/operations/AdminUsers"));
const AdminRoles = lazy(() => import("./pages/admin/operations/Roles"));
const AdminAuditLog = lazy(() => import("./pages/admin/operations/AuditLog"));
const AdminTasks = lazy(() => import("./pages/admin/operations/Tasks"));
const AdminShifts = lazy(() => import("./pages/admin/operations/Shifts"));
const AdminAgentPerformance = lazy(() => import("./pages/admin/operations/AgentPerformance"));
const AdminAnnouncements = lazy(() => import("./pages/admin/operations/Announcements"));
const AdminSLA = lazy(() => import("./pages/admin/operations/SLA"));
const AdminIncidents = lazy(() => import("./pages/admin/operations/Incidents"));
const AdminMessages = lazy(() => import("./pages/admin/operations/Messages"));

// --- AI ---
const AdminAIDashboard = lazy(() => import("./pages/admin/ai/Dashboard"));
const AdminDemandForecast = lazy(() => import("./pages/admin/ai/DemandForecast"));
const AdminSmartPricing = lazy(() => import("./pages/admin/ai/SmartPricing"));
const AdminChurnPrediction = lazy(() => import("./pages/admin/ai/ChurnPrediction"));
const AdminAnomalyDetection = lazy(() => import("./pages/admin/ai/AnomalyDetection"));
const AdminReplenishment = lazy(() => import("./pages/admin/ai/Replenishment"));
const AdminSalesInsights = lazy(() => import("./pages/admin/ai/SalesInsights"));
const AdminRecommendations = lazy(() => import("./pages/admin/ai/Recommendations"));
const AdminSentimentAnalysis = lazy(() => import("./pages/admin/ai/SentimentAnalysis"));
const AdminModelMonitor = lazy(() => import("./pages/admin/ai/ModelMonitor"));

// --- Analytics ---
const AdminAnalyticsOverview = lazy(() => import("./pages/admin/analytics/Overview"));
const AdminAnalyticsTrending = lazy(() => import("./pages/admin/analytics/Trending"));
const AdminAnalyticsSearch = lazy(() => import("./pages/admin/analytics/SearchAnalytics"));
const AdminAnalyticsFunnel = lazy(() => import("./pages/admin/analytics/Funnel"));
const AdminAnalyticsIntent = lazy(() => import("./pages/admin/analytics/Intent"));

// --- Content ---
const AdminContentManager = lazy(() => import("./pages/admin/content/ContentManager"));

// --- Operations Hub, Account Deletions & Error Dashboard ---
const AdminOperationsHub = lazy(() => import("./pages/admin/operations/OperationsHub"));
const AdminAccountDeletions = lazy(() => import("./pages/admin/operations/AccountDeletions"));
const AdminErrorDashboard = lazy(() => import("./pages/admin/operations/ErrorDashboard"));

// --- Yoboulmako ---
const YoboulmakoDashboard      = lazy(() => import("./pages/yoboulmako/Dashboard"));
const YoboulmakoLiveDeliveries = lazy(() => import("./pages/yoboulmako/LiveDeliveries"));
const YoboulmakoClients        = lazy(() => import("./pages/yoboulmako/Clients"));
const YoboulmakoBilling        = lazy(() => import("./pages/yoboulmako/Billing"));
const YoboulmakoFeatureFlags   = lazy(() => import("./pages/yoboulmako/FeatureFlags"));
const DispatchList             = lazy(() => import("./pages/yoboulmako/dispatcher/DispatchList"));
const DispatchMap              = lazy(() => import("./pages/yoboulmako/dispatcher/DispatchMap"));
const DispatchDetail           = lazy(() => import("./pages/yoboulmako/dispatcher/DispatchDetail"));

// --- Enterprise Hub ---
const AdminEnterpriseHub = lazy(() => import("./pages/admin/enterprise/EnterpriseHub"));

// --- Privacy ---
const AdminPrivacy = lazy(() => import("./pages/admin/privacy/Privacy"));

// --- Settings ---
const ModuleSettings = lazy(() => import("@/pages/admin/settings/ModuleSettings"));

// ---------------------------------------------------------------------------
// Driver Portal pages — Phase 3
// ---------------------------------------------------------------------------
const DriverLogin = lazy(() => import("@/pages/delivery-portal/DriverLogin"));
const DriverRegistration = lazy(() => import("@/pages/delivery-portal/DriverRegistration"));
const PendingApproval = lazy(() => import("@/pages/delivery-portal/PendingApproval"));
const DriverDashboard = lazy(() => import("@/pages/delivery-portal/DriverDashboard"));
const RouteView = lazy(() => import("@/pages/delivery-portal/RouteView"));
const DeliveryDetail = lazy(() => import("@/pages/delivery-portal/DeliveryDetail"));
const DeliveryHistory = lazy(() => import("@/pages/delivery-portal/DeliveryHistory"));
const DriverProfile = lazy(() => import("@/pages/delivery-portal/DriverProfile"));

// --- Launch Dashboard ---
const LaunchDashboard = lazy(() => import("./pages/admin/LaunchDashboard"));

// --- Enterprise ---
const AdminGDPR = lazy(() => import("./pages/admin/enterprise/GDPR"));
const AdminEInvoice = lazy(() => import("./pages/admin/enterprise/EInvoice"));
const AdminBackup = lazy(() => import("./pages/admin/enterprise/Backup"));
const AdminEnterpriseApiKeys = lazy(() => import("./pages/admin/enterprise/ApiKeys"));
const AdminWebhooks = lazy(() => import("./pages/admin/enterprise/Webhooks"));
const AdminSystemHealth = lazy(() => import("./pages/admin/enterprise/SystemHealth"));
const AdminPerformance = lazy(() => import("./pages/admin/enterprise/Performance"));
const AdminFeatureFlags = lazy(() => import("./pages/admin/enterprise/FeatureFlags"));
const AdminDataExport = lazy(() => import("./pages/admin/enterprise/DataExport"));
const AdminIntegrations = lazy(() => import("./pages/admin/enterprise/Integrations"));

// ---------------------------------------------------------------------------
// ChunkLoadError guard — runs once when the module first loads.
// After a new Vite deployment, old content-hashed chunk filenames become
// 404s. The dynamic import() rejection bubbles up as an unhandled rejection.
// We catch it here and reload once (guarded by sessionStorage) so the user
// automatically gets the latest build instead of seeing the 500 error page.
// ---------------------------------------------------------------------------
window.addEventListener("unhandledrejection", (event) => {
  const reason: unknown = event.reason;
  const isChunkError =
    reason instanceof Error &&
    (reason.message.includes("Failed to fetch dynamically imported module") ||
      reason.message.includes("Unable to preload CSS") ||
      reason.message.includes("error loading dynamically imported module") ||
      reason.name === "ChunkLoadError");

  if (isChunkError && !sessionStorage.getItem("chunk-reload-attempted")) {
    sessionStorage.setItem("chunk-reload-attempted", "1");
    window.location.reload();
  }
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-secondary border-t-transparent" />
  </div>
);

/**
 * Redirect trailing-slash URLs to their canonical non-trailing-slash equivalent.
 * Prevents duplicate content issues for SEO (e.g. /panier/ → /panier).
 * Only the root "/" is left untouched.
 */
const TrailingSlashRedirect = () => {
  const { pathname, search, hash } = useLocation();
  if (pathname !== "/" && pathname.endsWith("/")) {
    return <Navigate to={`${pathname.slice(0, -1)}${search}${hash}`} replace />;
  }
  return null;
};

// QueryClient factory — called once per component mount, avoids shared module-level state (SSR safe)
function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => handleQueryError(error, query),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _onMutate, mutation) => handleQueryError(error, mutation),
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 10 * 60_000,
        retry: (failureCount, error) => {
          // Never retry client errors (4xx) — they won't be fixed by retrying
          const status = (error as { status?: number })?.status;
          if (status !== undefined && status >= 400 && status < 500) return false;
          // Max 1 retry on server errors / network errors
          return failureCount < 1;
        },
        retryDelay: (attemptIndex) =>
          Math.min(1000 * 2 ** attemptIndex + Math.random() * 1000, 10000),
        refetchOnWindowFocus: false,
      },
    },
  });
}

const App = () => {
  const [queryClient] = useState(createQueryClient);

  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <FlagProvider userId="anonymous" role="customer">
      <TooltipProvider>
        <Toaster />
        <SonnerToaster />
        <BrowserRouter>
        <NuqsAdapter>
        <AnalyticsProvider>
        <CartLifecycle />
        <div className="max-w-[1920px] mx-auto">
          <TrailingSlashRedirect />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/panier" element={<Cart />} />
              <Route path="/livraison" element={<Livraison />} />
              <Route path="/commande" element={<Checkout />} />
              <Route path="/suivi/:ref" element={<Tracking />} />
              {/*
                Scaffold: driver proof-of-delivery webview. Currently
                renders a deny placeholder (DriverHandoff). Tracked
                under GitHub #104, Yoboulmako Phase C.
              */}
              <Route path="/driver/:token" element={<DriverHandoff />} />
              <Route path="/commandes/:orderId/recu" element={<Receipt />} />
              <Route path="/connexion" element={<Login />} />
              <Route path="/inscription" element={<Register />} />
              <Route path="/reinitialisation" element={<ResetRequest />} />
              <Route path="/reinitialisation/nouveau" element={<ResetPassword />} />
              <Route path="/compte" element={<AuthGuard><Profile /></AuthGuard>} />
              <Route path="/compte/profil" element={<AuthGuard><Profile /></AuthGuard>} />
              <Route path="/compte/commandes" element={<AuthGuard><Orders /></AuthGuard>} />
              <Route path="/compte/adresses" element={<AuthGuard><Addresses /></AuthGuard>} />
              <Route path="/compte/retours" element={<AuthGuard><Returns /></AuthGuard>} />
              <Route path="/compte/factures" element={<AuthGuard><Invoices /></AuthGuard>} />
              <Route path="/compte/favoris" element={<AuthGuard><Wishlist /></AuthGuard>} />
              <Route path="/compte/commande/:ref" element={<AuthGuard><OrderDetail /></AuthGuard>} />
              <Route path="/compte/parametres" element={<AuthGuard><AccountSettings /></AuthGuard>} />
              <Route path="/verification-email" element={<EmailVerification />} />
              <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/catalogue" element={<Catalogue />} />
              <Route path="/marque/:slug" element={<BrandPage />} />
              <Route path="/produit/:id" element={<ProductDetail />} />
              <Route path="/conditions" element={<Conditions />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/retours" element={<PolitiqueRetours />} />
              <Route path="/liste-souhaits/:token" element={<SharedWishlist />} />
              <Route path="/desinscription" element={<MarketingOptOut />} />

              {/* ============================================================ */}
              {/* Admin routes — protected by a SINGLE AdminGuard layout route */}
              {/* Auth check runs once and persists across tab navigation      */}
              {/* ============================================================ */}

              {/* Admin login — public route, no guard */}
              <Route path="/admin/connexion" element={<AdminLogin />} />

              {/* All admin routes share one AdminGuard + AdminAuthProvider instance */}
              <Route element={<AdminGuard />}>
                {/* Dashboard */}
                <Route path="/admin" element={<AdminDashboard />} />

                {/* Legacy French routes */}
                <Route path="/admin/clients" element={<AdminClients />} />
                <Route path="/admin/commandes" element={<AdminOrders />} />
                <Route path="/admin/finances" element={<AdminFinances />} />
                <Route path="/admin/logistique" element={<AdminLogistics />} />
                <Route path="/admin/retours" element={<AdminReturns />} />
                <Route path="/admin/factures" element={<AdminInvoices />} />
                <Route path="/admin/parametres" element={<AdminSettings />} />

                {/* --- DJAYMA engine consoles ------------------------------ */}
                <Route path="/admin/djayma" element={<DjaymaControlTower />} />
                <Route path="/admin/djayma/finance" element={<DjaymaFinance />} />
                <Route path="/admin/djayma/call-center" element={<DjaymaCallCenter />} />
                <Route path="/admin/djayma/stock" element={<DjaymaStock />} />
                <Route path="/admin/djayma/commandes" element={<DjaymaOrders />} />
                <Route path="/admin/djayma/logistique" element={<DjaymaLogistique />} />
                <Route path="/admin/djayma/prix" element={<DjaymaPricing />} />
                <Route path="/admin/djayma/promotions" element={<DjaymaPromos />} />
                <Route path="/admin/djayma/partenaires" element={<DjaymaPartners />} />
                <Route path="/admin/djayma/canaux" element={<DjaymaChannels />} />
                <Route path="/admin/djayma/acces" element={<DjaymaAccess />} />

                {/* --- Commerce -------------------------------------------- */}
                <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
                <Route path="/admin/orders" element={<AdminCommerceOrders />} />
                <Route path="/admin/products" element={<AdminProducts />} />
                <Route path="/admin/categories" element={<AdminCategories />} />
                <Route path="/admin/inventory" element={<AdminInventory />} />
                <Route path="/admin/stock-alerts" element={<AdminStockAlerts />} />
                <Route path="/admin/notifications" element={<AdminNotifications />} />

                {/* --- Payments -------------------------------------------- */}
                <Route path="/admin/payments" element={<AdminPayments />} />
                <Route path="/admin/reconciliation" element={<AdminModuleProvider><AdminReconciliation /></AdminModuleProvider>} />
                <Route path="/admin/refunds" element={<AdminModuleProvider><AdminRefunds /></AdminModuleProvider>} />
                <Route path="/admin/disputes" element={<AdminDisputes />} />
                <Route path="/admin/daily-cash" element={<AdminModuleProvider><AdminDailyCash /></AdminModuleProvider>} />
                <Route path="/admin/payments/callbacks" element={<AdminModuleProvider><AdminCallbackLogs /></AdminModuleProvider>} />
                <Route path="/admin/payments/dead-letter" element={<AdminModuleProvider><AdminDeadLetterQueue /></AdminModuleProvider>} />

                {/* --- Delivery -------------------------------------------- */}
                <Route path="/admin/deliveries" element={<AdminDeliveries />} />
                <Route path="/admin/drivers" element={<AdminDrivers />} />
                <Route path="/admin/routes" element={<AdminRoutes />} />
                <Route path="/admin/tracking" element={<AdminTracking />} />
                <Route path="/admin/yango" element={<AdminYango />} />
                <Route path="/admin/delivery-zones" element={<AdminModuleProvider><AdminDeliveryZones /></AdminModuleProvider>} />
                <Route path="/admin/delivery-slots" element={<AdminDeliverySlots />} />
                <Route path="/admin/depots" element={<AdminDepots />} />
                <Route path="/admin/delivery-performance" element={<AdminDeliveryPerformance />} />
                <Route path="/admin/delivery/flags" element={<AdminDeliveryFlags />} />

                {/* --- B2B ------------------------------------------------- */}
                {/* Index redirect so /admin/b2b (sidebar root link) lands
                    on the Customers sub-page instead of silently falling
                    through to the dashboard. */}
                <Route path="/admin/b2b" element={<Navigate to="/admin/b2b/customers" replace />} />
                <Route path="/admin/b2b/customers" element={<AdminB2BCustomers />} />
                <Route path="/admin/b2b/price-lists" element={<AdminB2BPriceLists />} />
                <Route path="/admin/b2b/credit" element={<AdminB2BCredit />} />
                <Route path="/admin/b2b/approvals" element={<AdminB2BApprovals />} />
                <Route path="/admin/b2b/import" element={<AdminB2BImport />} />
                <Route path="/admin/b2b/invoices" element={<AdminB2BInvoices />} />
                <Route path="/admin/b2b/aging" element={<AdminB2BAging />} />
                <Route path="/admin/suppliers" element={<AdminSuppliers />} />
                <Route path="/admin/b2b/volume-discounts" element={<AdminB2BVolumeDiscounts />} />
                <Route path="/admin/b2b/reps" element={<AdminB2BReps />} />

                {/* --- CRM ------------------------------------------------- */}
                <Route path="/admin/segments" element={<AdminSegments />} />
                <Route path="/admin/loyalty" element={<AdminLoyalty />} />
                <Route path="/admin/whatsapp" element={<AdminWhatsApp />} />
                <Route path="/admin/sms" element={<AdminSMS />} />
                <Route path="/admin/feedback" element={<AdminFeedback />} />
                <Route path="/admin/abandoned-carts" element={<AdminAbandonedCarts />} />
                <Route path="/admin/clv" element={<AdminCLV />} />
                <Route path="/admin/referrals" element={<AdminReferrals />} />
                <Route path="/admin/communications" element={<AdminCommunications />} />

                {/* --- Marketing ------------------------------------------- */}
                <Route path="/admin/promotions" element={<AdminPromotions />} />
                <Route path="/admin/coupons" element={<AdminCoupons />} />
                <Route path="/admin/flash-sales" element={<AdminFlashSales />} />
                <Route path="/admin/bundles" element={<AdminBundles />} />
                <Route path="/admin/banners" element={<AdminBanners />} />
                <Route path="/admin/newsletter" element={<AdminNewsletter />} />
                <Route path="/admin/seasonal" element={<AdminSeasonal />} />
                <Route path="/admin/stacking-rules" element={<AdminStackingRules />} />
                <Route path="/admin/promo-analytics" element={<AdminPromoAnalytics />} />
                <Route path="/admin/tracking-links" element={<AdminTrackingLinks />} />

                {/* --- Finance -------------------------------------------- */}
                <Route path="/admin/revenue" element={<AdminRevenue />} />
                <Route path="/admin/tva" element={<AdminTVA />} />
                <Route path="/admin/margins" element={<AdminMargins />} />
                <Route path="/admin/expenses" element={<AdminExpenses />} />
                <Route path="/admin/invoices" element={<AdminFinanceInvoices />} />
                <Route path="/admin/cashflow" element={<AdminCashflow />} />
                <Route path="/admin/sage" element={<AdminSageExport />} />
                <Route path="/admin/powerbi" element={<AdminPowerBI />} />
                <Route path="/admin/reports" element={<AdminReports />} />
                <Route path="/admin/daily-pnl" element={<AdminDailyPnL />} />

                {/* --- Operations ----------------------------------------- */}
                <Route path="/admin/admin-users" element={<AdminAdminUsers />} />
                <Route path="/admin/roles" element={<AdminRoles />} />
                <Route path="/admin/audit-log" element={<AdminAuditLog />} />
                <Route path="/admin/tasks" element={<AdminTasks />} />
                <Route path="/admin/shifts" element={<AdminShifts />} />
                <Route path="/admin/agent-performance" element={<AdminAgentPerformance />} />
                <Route path="/admin/announcements" element={<AdminAnnouncements />} />
                <Route path="/admin/sla" element={<AdminSLA />} />
                <Route path="/admin/incidents" element={<AdminIncidents />} />
                <Route path="/admin/messages" element={<AdminMessages />} />

                {/* --- Enterprise ----------------------------------------- */}
                <Route path="/admin/gdpr" element={<AdminGDPR />} />
                <Route path="/admin/einvoice" element={<AdminEInvoice />} />
                <Route path="/admin/backup" element={<AdminBackup />} />
                <Route path="/admin/api-keys" element={<AdminEnterpriseApiKeys />} />
                <Route path="/admin/webhooks" element={<AdminWebhooks />} />
                <Route path="/admin/system-health" element={<AdminSystemHealth />} />
                <Route path="/admin/performance" element={<AdminPerformance />} />
                <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
                <Route path="/admin/data-export" element={<AdminDataExport />} />
                <Route path="/admin/integrations" element={<AdminIntegrations />} />

                {/* --- AI ------------------------------------------------- */}
                <Route path="/admin/ai" element={<AdminAIDashboard />} />
                <Route path="/admin/ai/forecast" element={<AdminDemandForecast />} />
                <Route path="/admin/ai/smart-pricing" element={<AdminSmartPricing />} />
                <Route path="/admin/ai/churn" element={<AdminChurnPrediction />} />
                <Route path="/admin/ai/anomalies" element={<AdminAnomalyDetection />} />
                <Route path="/admin/ai/replenishment" element={<AdminReplenishment />} />
                <Route path="/admin/ai/insights" element={<AdminSalesInsights />} />
                <Route path="/admin/ai/recommendations" element={<AdminRecommendations />} />
                <Route path="/admin/ai/sentiment" element={<AdminSentimentAnalysis />} />
                <Route path="/admin/ai/models" element={<AdminModelMonitor />} />

                {/* --- Analytics ------------------------------------------ */}
                <Route path="/admin/analytics" element={<AdminAnalyticsOverview />} />
                <Route path="/admin/analytics/trending" element={<AdminAnalyticsTrending />} />
                {/* Tolerate the /trends typo and redirect to /trending. */}
                <Route path="/admin/analytics/trends" element={<Navigate to="/admin/analytics/trending" replace />} />
                <Route path="/admin/analytics/search" element={<AdminAnalyticsSearch />} />
                <Route path="/admin/analytics/funnel" element={<AdminAnalyticsFunnel />} />
                <Route path="/admin/analytics/intent" element={<AdminAnalyticsIntent />} />

                {/* --- Content -------------------------------------------- */}
                <Route path="/admin/contenu" element={<AdminContentManager />} />

                {/* --- Operations Hub + Account Deletions + Error Dashboard -- */}
                <Route path="/admin/operations" element={<AdminOperationsHub />} />
                <Route path="/admin/operations/account-deletions" element={<AdminAccountDeletions />} />
                <Route path="/admin/errors" element={<AdminErrorDashboard />} />

                {/* --- Privacy -------------------------------------------- */}
                <Route path="/admin/vie-privee" element={<AdminPrivacy />} />

                {/* --- Enterprise Hub ------------------------------------- */}
                <Route path="/admin/entreprise" element={<AdminEnterpriseHub />} />

                {/* --- Settings ------------------------------------------- */}
                <Route path="/admin/modules" element={<AdminModuleProvider><ModuleSettings /></AdminModuleProvider>} />

                {/* --- Yoboulmako --------------------------------------------- */}
                <Route path="/admin/yoboulmako" element={<YoboulmakoDashboard />} />
                <Route path="/admin/yoboulmako/livraisons" element={<YoboulmakoLiveDeliveries />} />
                <Route path="/admin/yoboulmako/clients" element={<YoboulmakoClients />} />
                <Route path="/admin/yoboulmako/facturation" element={<YoboulmakoBilling />} />
                <Route path="/admin/yoboulmako/parametres" element={<YoboulmakoFeatureFlags />} />
                <Route path="/admin/yoboulmako/dispatches" element={<DispatchList />} />
                <Route path="/admin/yoboulmako/dispatches/map" element={<DispatchMap />} />
                <Route path="/admin/yoboulmako/dispatches/:ref" element={<DispatchDetail />} />

                {/* --- Launch Dashboard ----------------------------------- */}
                <Route path="/admin/lancement" element={<LaunchDashboard />} />
              </Route>

              {/* ============================================================ */}
              {/* Driver Portal — /livreur/*                                  */}
              {/* Public routes are unguarded; protected routes use           */}
              {/* DriverAuthGuard as a layout route (renders <Outlet />)      */}
              {/* ============================================================ */}

              {/* Driver Portal — Public routes */}
              <Route path="/livreur/connexion" element={<DriverLogin />} />
              <Route path="/livreur/inscription" element={<DriverRegistration />} />
              <Route path="/livreur/inscription-soumise" element={<PendingApproval />} />

              {/* Driver Portal — Protected routes */}
              <Route path="/livreur" element={<DriverAuthGuard />}>
                <Route index element={<DriverDashboard />} />
                <Route path="tournee" element={<RouteView />} />
                <Route path="livraison/:id" element={<DeliveryDetail />} />
                <Route path="historique" element={<DeliveryHistory />} />
                <Route path="profil" element={<DriverProfile />} />
              </Route>

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          {/* Widgets are lazy — null fallback avoids layout shift while the chunk loads */}
          <Suspense fallback={null}>
            <CookieConsent />
          </Suspense>
          <Suspense fallback={null}>
            <ChatWidget />
          </Suspense>
        </div>
        </AnalyticsProvider>
        </NuqsAdapter>
        </BrowserRouter>
      </TooltipProvider>
      </FlagProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
